import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { Loader2, Upload, X, Check, ChevronLeft, ChevronRight, Search, Plus, Image as ImageIcon, ClipboardPaste, ChevronDown, Save, FileText, Trash2, Download, UserCheck, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { COUNTRIES, getCountryName } from '../../utils/countries';
import ClipboardPasteModal from '../../components/ClipboardPasteModal';
import { FormField, FormSelect, MultiSelectCheckboxes, ReviewSection, WizardStepper, OrangeCheckbox, ReviewProductHeader } from '../shared/WizardComponents';
import { useDownload, formatBytes } from '../../context/DownloadContext';
import { SubmittalPreviewModal } from '../../components/SubmittalPreviewModal';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useSubmittalsManager, commitSubmittals } from '../shared/useSubmittalsManager';
import { WizardSubmittalsStep, WizardSubmittalsReview } from '../shared/WizardSubmittalsStep';
import { Badge } from '../../components/ui/badge';
import { ZoomControls } from '../shared/ElevationViewer';

const AB_MARKER_TYPES = ['Roof–wall', 'Window perimeter', 'Slab edge'];
const AB_MARKER_META = {
  'Roof–wall':        { color: '#3b82f6', shape: 'rounded-sm',   hoverBg: 'bg-blue-500 hover:bg-blue-600' },
  'Window perimeter': { color: '#ea580c', shape: 'rounded-full', hoverBg: 'bg-orange-500 hover:bg-orange-600' },
  'Slab edge':        { color: '#16a34a', shape: 'rounded-full', hoverBg: 'bg-green-500 hover:bg-green-600' },
};

const WIZARD_STEPS = [
  { id: 1, title: 'Product Information' },
  { id: 2, title: 'Air Leakage Performance' },
  { id: 3, title: 'Continuity & System Components' },
  { id: 4, title: 'Substrate & Compatibility' },
  { id: 5, title: 'Installation Parameters' },
  { id: 6, title: 'Fire & Code Compliance' },
  { id: 7, title: 'Interface Responsibility' },
  { id: 8, title: 'Testing & Field Verification' },
  { id: 9, title: 'Submittals' },
  { id: 10, title: 'Visual Coverage' },
  { id: 11, title: 'Review Summary' },
];
const TOTAL_STEPS = WIZARD_STEPS.length;

const AIR_BARRIER_TYPES = ['None', 'Fluid-applied', 'Sheet-applied', 'Self-adhered', 'Integrated system'];
const ROLE_OPTIONS = ['None', 'Air Barrier only', 'Air + WRB', 'Air + Vapor control'];
const APPLICATION_LOCATIONS = ['None', 'Above-grade wall', 'Roof transition', 'Below-grade (limited)'];
const LEAKAGE_TEST_STANDARDS = ['None', 'ASTM E2178 (material)', 'ASTM E2357 (assembly)'];
const REQUIRED_ACCESSORIES = ['Transition membrane', 'Liquid flashing', 'Sealant'];
const APPROVED_SUBSTRATES = ['None', 'Gypsum sheathing', 'CMU', 'Concrete', 'Plywood'];
const COMPATIBILITY_WITH_OPTIONS = ['None', 'Insulation', 'Sealants', 'WRB'];
const APPLICATION_METHODS = ['Spray', 'Roller', 'Trowel', 'Peel & Stick', 'Other'];
const INTERFACES_WITH_OPTIONS = ['Windows / Curtain Wall', 'Roofing', 'Waterproofing', 'Insulation (CI)', 'Other'];
const CONTINUITY_RESP_OPTIONS = ['This subcontractor', 'Other'];
const DOCUMENT_TYPES = ['Product Data Sheet', 'Test Report - E2178', 'Test Report - E2357', 'Installation Guide', 'Warranty', 'Compatibility Letters'];
const FIELD_TESTING_OPTIONS = ['Adhesion test', 'Visual inspection', 'Blower door (whole building)'];

const INITIAL_FORM = {
  manufacturerId: '', productNameModel: '', countryCode: '',
  imageFile: null, imagePreview: null,
  airBarrierType: '', roleOfProduct: '', applicationLocation: [],
  airLeakagePerformance: { airPermeanceValue: '', testStandard: '', testPressure: '', complianceStatement: '' },
  continuityComponents: { requiredAccessories: [], compatibleProducts: '', continuityMethodDescription: '', discontinuityRiskZones: '' },
  substrateCompatibility: { approvedSubstrates: '', primerRequired: '', compatibilityWith: '', compatibilityLetterSubmittal: null },
  installationParameters: { applicationMethod: '', applicationMethodOther: '', wetMilThickness: '', cureTime: '', installationTempRange: '', maxExposureDays: '' },
  fireCodeCompliance: { nfpa285Compatibility: '', referencedWallAssemblies: '', astmE84: '', iccESReport: '' },
  interfaceResponsibility: { interfacesWith: '', interfacesWithOther: '', continuityResponsibility: '', otherSubcontractor: '', transitionOwnershipConfirmation: false },
  testingVerification: { mockupRequired: '', fieldTesting: [], testAgency: '', passFailStatus: '', testResultSubmittal: null },
  visualCoverage: { elevationFileName: '', elevationUrl: '', elevationPublicId: '', markers: [], markerType: '' },
  submittals: [],
};

const STORAGE_PREFIX = 'specard_ab_wizard_v1_';
const getStorageKey = (companyId, draftId) => `${STORAGE_PREFIX}${companyId}_${draftId || 'new'}`;
const saveWizardState = (companyId, draftId, state) => {
  try {
    const f = state.form;
    sessionStorage.setItem(getStorageKey(companyId, draftId), JSON.stringify({
      step: state.step, draftId: state.draftId,
      form: {
        manufacturerId: f.manufacturerId, productNameModel: f.productNameModel, countryCode: f.countryCode,
        imagePreview: f.imagePreview && !f.imagePreview.startsWith('blob:') ? f.imagePreview : null,
        airBarrierType: f.airBarrierType, roleOfProduct: f.roleOfProduct, applicationLocation: f.applicationLocation,
        airLeakagePerformance: f.airLeakagePerformance, continuityComponents: f.continuityComponents,
        substrateCompatibility: f.substrateCompatibility, installationParameters: f.installationParameters,
        fireCodeCompliance: f.fireCodeCompliance, interfaceResponsibility: f.interfaceResponsibility,
        testingVerification: f.testingVerification,
        visualCoverage: f.visualCoverage,
        submittals: f.submittals,
      },
    }));
  } catch { /* quota */ }
};
const loadWizardState = (companyId, draftId) => { try { const r = sessionStorage.getItem(getStorageKey(companyId, draftId)); return r ? JSON.parse(r) : null; } catch { return null; } };
const clearWizardState = (companyId, draftId) => { try { sessionStorage.removeItem(getStorageKey(companyId, draftId)); if (draftId) sessionStorage.removeItem(getStorageKey(companyId, 'new')); } catch {} };

const mergeFormDefaults = (saved) => {
  if (!saved) return { ...INITIAL_FORM };
  return {
    ...INITIAL_FORM, ...saved,
    applicationLocation: saved.applicationLocation || [],
    airLeakagePerformance: { ...INITIAL_FORM.airLeakagePerformance, ...(saved.airLeakagePerformance || {}) },    continuityComponents: { ...INITIAL_FORM.continuityComponents, ...(saved.continuityComponents || {}), requiredAccessories: saved.continuityComponents?.requiredAccessories || [] },
    substrateCompatibility: { ...INITIAL_FORM.substrateCompatibility, ...(saved.substrateCompatibility || {}) },
    installationParameters: { ...INITIAL_FORM.installationParameters, ...(saved.installationParameters || {}) },
    fireCodeCompliance: { ...INITIAL_FORM.fireCodeCompliance, ...(saved.fireCodeCompliance || {}) },
    interfaceResponsibility: { ...INITIAL_FORM.interfaceResponsibility, ...(saved.interfaceResponsibility || {}) },
    testingVerification: { ...INITIAL_FORM.testingVerification, ...(saved.testingVerification || {}), fieldTesting: saved.testingVerification?.fieldTesting || [] },
    visualCoverage: { ...INITIAL_FORM.visualCoverage, ...(saved.visualCoverage || {}), markers: saved.visualCoverage?.markers || [] },
    submittals: saved.submittals || [],
  };
};

const isNumericValue = (v) => !v || /^-?\d*\.?\d*$/.test(v.trim());
const sanitizeNumeric = (v) => v.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
const sanitizeFraction = (v) => v.replace(/[^0-9./]/g, '');

export const AddAirBarrierProductWizard = ({ open, onOpenChange, onComplete, onRefresh, editProduct, companyId, projectId, csiItemId }) => {
  const isEdit = !!editProduct;
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const initialFormRef = useRef(INITIAL_FORM);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteUploading, setPasteUploading] = useState(false);
  const { startDownload, cancelDownload, getState: getDlState } = useDownload();

  const [manufacturers, setManufacturers] = useState([]);
  const [mfrSearch, setMfrSearch] = useState('');
  const [mfrDropdownOpen, setMfrDropdownOpen] = useState(false);
  const [creatingMfr, setCreatingMfr] = useState(false);
  const mfrRef = useRef(null);

  const [countrySearch, setCountrySearch] = useState('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const countryRef = useRef(null);

  const [uploadingCompatLetter, setUploadingCompatLetter] = useState(false);
  const [uploadingTestResult, setUploadingTestResult] = useState(false);
  const [previewSubmittal, setPreviewSubmittal] = useState(null);
  const [elevationUploading, setElevationUploading] = useState(false);
  const [elevationZoom, setElevationZoom] = useState(1);
  const compatLetterRef = useRef(null);
  const testResultFileRef = useRef(null);
  const fileInputRef = useRef(null);
  const initializedRef = useRef(false);
  const submittals = useSubmittalsManager();

  useEffect(() => {
    if (!open || initializedRef.current) return;
    initializedRef.current = true;
    if (editProduct) {
      const editForm = mergeFormDefaults({
        manufacturerId: editProduct.manufacturerId || '', productNameModel: editProduct.productNameModel || '',
        countryCode: editProduct.countryCode || '', imageFile: null,
        imagePreview: editProduct.imageUrl || editProduct.imageCloudinary?.urls?.medium || null,
        airBarrierType: editProduct.airBarrierType || '', roleOfProduct: editProduct.roleOfProduct || '',
        applicationLocation: editProduct.applicationLocation || [],
        airLeakagePerformance: editProduct.airLeakagePerformance, continuityComponents: editProduct.continuityComponents,
        substrateCompatibility: editProduct.substrateCompatibility, installationParameters: editProduct.installationParameters,
        fireCodeCompliance: editProduct.fireCodeCompliance, interfaceResponsibility: editProduct.interfaceResponsibility,
        testingVerification: editProduct.testingVerification,
        visualCoverage: editProduct.visualCoverage,
        submittals: editProduct.submittals || [],
        qrToken: editProduct.qrToken,
      });
      setForm(editForm);
      initialFormRef.current = mergeFormDefaults({ ...editForm, qrToken: undefined });
      setDraftId(editProduct.id);
      submittals.init(editProduct.submittals || []);
      setStep(1);
    } else {
      clearWizardState(companyId, 'new');
      setForm({ ...INITIAL_FORM }); setStep(1); setDraftId(null); initialFormRef.current = { ...INITIAL_FORM };
      submittals.init([]);
    }
  }, [open, editProduct, companyId]);

  useEffect(() => {
    if (!open || !initializedRef.current) return;
    saveWizardState(companyId, draftId || (isEdit ? editProduct?.id : 'new'), { step, draftId, form });
  }, [step, form, draftId, open, companyId, isEdit, editProduct]);

  const isDirty = (() => {
    if (submittals.isDirty) return true;
    const i = initialFormRef.current;
    return form.productNameModel !== i.productNameModel || form.manufacturerId !== i.manufacturerId ||
      form.countryCode !== i.countryCode || !!form.imageFile ||
      form.airBarrierType !== i.airBarrierType || form.roleOfProduct !== i.roleOfProduct ||
      JSON.stringify(form.applicationLocation) !== JSON.stringify(i.applicationLocation) ||
      JSON.stringify(form.airLeakagePerformance) !== JSON.stringify(i.airLeakagePerformance) ||
      JSON.stringify(form.continuityComponents) !== JSON.stringify(i.continuityComponents) ||
      JSON.stringify(form.substrateCompatibility) !== JSON.stringify(i.substrateCompatibility) ||
      JSON.stringify(form.installationParameters) !== JSON.stringify(i.installationParameters) ||
      JSON.stringify(form.fireCodeCompliance) !== JSON.stringify(i.fireCodeCompliance) ||
      JSON.stringify(form.interfaceResponsibility) !== JSON.stringify(i.interfaceResponsibility) ||
      JSON.stringify(form.testingVerification) !== JSON.stringify(i.testingVerification) ||
      JSON.stringify(form.visualCoverage) !== JSON.stringify(i.visualCoverage);
  })();

  const fetchManufacturers = useCallback(async () => { try { const res = await axios.get(`${API}/thermal-insulation-manufacturers`); setManufacturers(res.data); } catch {} }, []);
  useEffect(() => { fetchManufacturers(); }, [fetchManufacturers]);

  useEffect(() => {
    const h = (e) => { if (mfrRef.current && !mfrRef.current.contains(e.target)) setMfrDropdownOpen(false); if (countryRef.current && !countryRef.current.contains(e.target)) setCountryDropdownOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selectedManufacturer = manufacturers.find(m => m.id === form.manufacturerId);
  const filteredManufacturers = manufacturers.filter(m => m.name.toLowerCase().includes(mfrSearch.toLowerCase()));
  const showAddNew = mfrSearch.trim() && !filteredManufacturers.some(m => m.name.toLowerCase() === mfrSearch.trim().toLowerCase());
  const filteredCountries = COUNTRIES.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()) || c.code.toLowerCase().includes(countrySearch.toLowerCase()));
  const selectedCountry = COUNTRIES.find(c => c.code === form.countryCode);
  const isReviewStep = step === TOTAL_STEPS;

  const badge = (val, pos = ['Yes', 'Pass', 'Confirmed'], neg = ['No', 'Fail', 'Not confirmed']) => {
    if (!val) return '—';
    const isPos = pos.includes(val);
    const isNeg = neg.includes(val);
    if (!isPos && !isNeg) return val;
    return <span className={`inline-flex items-center px-2 py-0.5 rounded ${isPos ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>{val}</span>;
  };

  const updateNested = (section, field, value) => { setForm(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } })); setErrors(prev => ({ ...prev, [`${section}.${field}`]: null })); };

  // Smart: Clear otherSubcontractor when not Other
  // Inner prev check guards against stale-closure race with init effect
  useEffect(() => {
    if (form.interfaceResponsibility.continuityResponsibility !== 'Other') {
      setForm(prev => {
        if (prev.interfaceResponsibility.continuityResponsibility !== 'Other' && prev.interfaceResponsibility.otherSubcontractor) {
          return { ...prev, interfaceResponsibility: { ...prev.interfaceResponsibility, otherSubcontractor: '' } };
        }
        return prev;
      });
    }
  }, [form.interfaceResponsibility.continuityResponsibility]);

  // Smart: Clear interfacesWithOther when not Other
  useEffect(() => {
    if (form.interfaceResponsibility.interfacesWith !== 'Other') {
      setForm(prev => {
        if (prev.interfaceResponsibility.interfacesWith !== 'Other' && prev.interfaceResponsibility.interfacesWithOther) {
          return { ...prev, interfaceResponsibility: { ...prev.interfaceResponsibility, interfacesWithOther: '' } };
        }
        return prev;
      });
    }
  }, [form.interfaceResponsibility.interfacesWith]);

  // Smart: Clear applicationMethodOther when not Other
  useEffect(() => {
    if (form.installationParameters.applicationMethod !== 'Other') {
      setForm(prev => {
        if (prev.installationParameters.applicationMethod !== 'Other' && prev.installationParameters.applicationMethodOther) {
          return { ...prev, installationParameters: { ...prev.installationParameters, applicationMethodOther: '' } };
        }
        return prev;
      });
    }
  }, [form.installationParameters.applicationMethod]);

  const handleCreateManufacturer = async () => {
    const name = mfrSearch.trim(); if (!name) return;
    setCreatingMfr(true);
    try { const res = await axios.post(`${API}/thermal-insulation-manufacturers`, { name }); setManufacturers(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name))); setForm(prev => ({ ...prev, manufacturerId: res.data.id })); setMfrSearch(''); setMfrDropdownOpen(false); toast.success(`Manufacturer "${name}" created`); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to create manufacturer'); }
    finally { setCreatingMfr(false); }
  };
  const handleSelectManufacturer = (m) => { setForm(prev => ({ ...prev, manufacturerId: m.id })); setMfrSearch(''); setMfrDropdownOpen(false); };
  const handleSelectCountry = (c) => { setForm(prev => ({ ...prev, countryCode: c.code })); setCountrySearch(''); setCountryDropdownOpen(false); };

  const applyImageFile = (file) => {
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) { toast.error('Only PNG, JPEG, and WebP images are allowed'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be less than 5MB'); return; }
    if (form.imagePreview?.startsWith('blob:')) URL.revokeObjectURL(form.imagePreview);
    setForm(prev => ({ ...prev, imageFile: file, imagePreview: URL.createObjectURL(file) }));
  };
  const handleImageSelect = (e) => { if (e.target.files[0]) applyImageFile(e.target.files[0]); };
  const handlePasteUpload = async (f) => { setPasteUploading(true); try { applyImageFile(f); setShowPasteModal(false); } finally { setPasteUploading(false); } };
  const handleRemoveImage = () => { if (form.imagePreview?.startsWith('blob:')) URL.revokeObjectURL(form.imagePreview); setForm(prev => ({ ...prev, imageFile: null, imagePreview: null })); if (fileInputRef.current) fileInputRef.current.value = ''; };

  // Auto-draft helper: creates draft if needed before upload
  const ensureDraftForUpload = async () => {
    if (draftId) return draftId;
    if (!form.productNameModel.trim() || !form.manufacturerId) { toast.error('Please fill in Product Name and Manufacturer first'); setStep(1); return null; }
    setSaving(true);
    try {
      const payload = { manufacturerId: form.manufacturerId, productNameModel: form.productNameModel.trim(), countryCode: form.countryCode, csiItemId, status: 'draft' };
      const res = await axios.post(`${API}/air-barrier-products/${projectId}`, payload);
      const pid = res.data.id; setDraftId(pid); setForm(prev => ({ ...prev, qrToken: res.data.qrToken }));
      clearWizardState(companyId, 'new'); if (onRefresh) onRefresh();
      return pid;
    } catch (err) { toast.error('Failed to create draft for upload'); return null; }
    finally { setSaving(false); }
  };

  const handleCompatLetterUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error('Only PDF files are allowed'); if (compatLetterRef.current) compatLetterRef.current.value = ''; return; }
    const pid = await ensureDraftForUpload();
    if (!pid) return;
    setUploadingCompatLetter(true);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('document_type', 'Compatibility Letters');
      const res = await axios.post(`${API}/air-barrier-products/${projectId}/${pid}/upload-submittal`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const newSub = res.data;
      setForm(prev => ({ ...prev, substrateCompatibility: { ...prev.substrateCompatibility, compatibilityLetterSubmittal: newSub } }));
      submittals.init([...submittals.committed, newSub]);
      toast.success('Compatibility letter uploaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to upload'); }
    finally { setUploadingCompatLetter(false); if (compatLetterRef.current) compatLetterRef.current.value = ''; }
  };
  const handleTestResultUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error('Only PDF files are allowed'); if (testResultFileRef.current) testResultFileRef.current.value = ''; return; }
    const pid = await ensureDraftForUpload();
    if (!pid) return;
    setUploadingTestResult(true);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('document_type', 'Test Result');
      const res = await axios.post(`${API}/air-barrier-products/${projectId}/${pid}/upload-submittal`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const newSub = res.data;
      setForm(prev => ({ ...prev, testingVerification: { ...prev.testingVerification, testResultSubmittal: newSub } }));
      submittals.init([...submittals.committed, newSub]);
      toast.success('Test result uploaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to upload'); }
    finally { setUploadingTestResult(false); if (testResultFileRef.current) testResultFileRef.current.value = ''; }
  };
  const handleDeleteSubmittal = async (id) => {
    if (!draftId) return;
    try { await axios.delete(`${API}/air-barrier-products/${projectId}/${draftId}/submittals/${id}`);
      submittals.init(submittals.committed.filter(s => s.id !== id));
      setForm(prev => ({
        ...prev,
        substrateCompatibility: { ...prev.substrateCompatibility, ...(prev.substrateCompatibility.compatibilityLetterSubmittal?.id === id ? { compatibilityLetterSubmittal: null } : {}) },
        testingVerification: { ...prev.testingVerification, ...(prev.testingVerification.testResultSubmittal?.id === id ? { testResultSubmittal: null } : {}) },
      }));
      toast.success('Document removed'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove document'); }
  };
  const handlePreviewSubmittal = async (submittal) => {
    if (!draftId || submittal._status === 'pending') return;
    try {
      const res = await axios.get(`${API}/air-barrier-products/${projectId}/${draftId}/submittals/${submittal.id}/preview-url`);
      setPreviewSubmittal({ ...submittal, cloudinaryUrl: res.data?.url });
    } catch { toast.error('Failed to load preview'); }
  };

  const validateDraft = () => {
    const e = {};
    if (!form.productNameModel.trim()) e.productNameModel = 'Product Name / Model is required';
    setErrors(e); return !Object.keys(e).length;
  };
  const validateAllSteps = () => {
    const e = {};
    if (!form.productNameModel.trim()) e.productNameModel = 'Product Name / Model is required';
    if (!form.manufacturerId) e.manufacturerId = 'Manufacturer is required';
    if (form.airLeakagePerformance.airPermeanceValue && !isNumericValue(form.airLeakagePerformance.airPermeanceValue)) e['airLeakagePerformance.airPermeanceValue'] = 'Must be numeric';
    if (form.airLeakagePerformance.testPressure && !isNumericValue(form.airLeakagePerformance.testPressure)) e['airLeakagePerformance.testPressure'] = 'Must be numeric';
    if (!form.substrateCompatibility.primerRequired) e['substrateCompatibility.primerRequired'] = 'Primer Required is required';
    if (form.installationParameters.maxExposureDays && !isNumericValue(form.installationParameters.maxExposureDays)) e['installationParameters.maxExposureDays'] = 'Must be numeric';
    if (form.installationParameters.applicationMethod === 'Other' && !form.installationParameters.applicationMethodOther?.trim()) e['installationParameters.applicationMethodOther'] = 'Please specify the application method';
    if (form.airBarrierType === 'Fluid-applied' && !form.installationParameters.wetMilThickness?.trim()) e['installationParameters.wetMilThickness'] = 'Wet Mil Thickness is required for Fluid-applied';
    if (!form.fireCodeCompliance.nfpa285Compatibility) e['fireCodeCompliance.nfpa285Compatibility'] = 'NFPA 285 Compatibility is required';
    if (form.interfaceResponsibility.interfacesWith === 'Other' && !form.interfaceResponsibility.interfacesWithOther?.trim()) e['interfaceResponsibility.interfacesWithOther'] = 'Please specify the interface';
    if (!form.interfaceResponsibility.continuityResponsibility) e['interfaceResponsibility.continuityResponsibility'] = 'Continuity Responsibility is required';
    if (form.interfaceResponsibility.continuityResponsibility === 'Other' && !form.interfaceResponsibility.otherSubcontractor?.trim()) e['interfaceResponsibility.otherSubcontractor'] = 'Please specify the subcontractor';
    if (!form.testingVerification.mockupRequired) e['testingVerification.mockupRequired'] = 'Mock-up Required is required';
    if (!form.testingVerification.passFailStatus) e['testingVerification.passFailStatus'] = 'Pass/Fail Status is required';
    // Step 9 — Submittals (optional)
    setErrors(e);
    return e;
  };
  const getFirstErrorStep = (e) => {
    if (['productNameModel', 'manufacturerId', 'countryCode', 'imageFile'].some(f => e[f])) return 1;
    if (Object.keys(e).some(k => k.startsWith('airLeakagePerformance'))) return 2;
    if (Object.keys(e).some(k => k.startsWith('continuityComponents'))) return 3;
    if (Object.keys(e).some(k => k.startsWith('substrateCompatibility'))) return 4;
    if (Object.keys(e).some(k => k.startsWith('installationParameters'))) return 5;
    if (Object.keys(e).some(k => k.startsWith('fireCodeCompliance'))) return 6;
    if (Object.keys(e).some(k => k.startsWith('interfaceResponsibility'))) return 7;
    if (Object.keys(e).some(k => k.startsWith('testingVerification'))) return 8;
    if (e.submittals) return 9;
    return 1;
  };

  const buildStepPayload = () => {
    const p = {};
    const alp = form.airLeakagePerformance; if (alp.airPermeanceValue || alp.testStandard || alp.testPressure || alp.complianceStatement) p.airLeakagePerformance = alp;
    const cc = form.continuityComponents; if (cc.requiredAccessories?.length || cc.compatibleProducts || cc.continuityMethodDescription || cc.discontinuityRiskZones) p.continuityComponents = cc;
    const sc = form.substrateCompatibility; if (sc.approvedSubstrates || sc.primerRequired || sc.compatibilityWith || sc.compatibilityLetterSubmittal) p.substrateCompatibility = { approvedSubstrates: sc.approvedSubstrates, primerRequired: sc.primerRequired, compatibilityWith: sc.compatibilityWith };
    const ip = form.installationParameters; if (ip.applicationMethod || ip.wetMilThickness || ip.cureTime || ip.installationTempRange || ip.maxExposureDays) p.installationParameters = { applicationMethod: ip.applicationMethod, applicationMethodOther: ip.applicationMethodOther, wetMilThickness: ip.wetMilThickness, cureTime: ip.cureTime, installationTempRange: ip.installationTempRange, maxExposureDays: ip.maxExposureDays };
    const fcc = form.fireCodeCompliance; if (fcc.nfpa285Compatibility || fcc.referencedWallAssemblies || fcc.astmE84 || fcc.iccESReport) p.fireCodeCompliance = fcc;
    const ir = form.interfaceResponsibility; if (ir.interfacesWith || ir.continuityResponsibility || ir.transitionOwnershipConfirmation) p.interfaceResponsibility = { interfacesWith: ir.interfacesWith, interfacesWithOther: ir.interfacesWithOther, continuityResponsibility: ir.continuityResponsibility, otherSubcontractor: ir.otherSubcontractor, transitionOwnershipConfirmation: ir.transitionOwnershipConfirmation };
    const tv = form.testingVerification; if (tv.mockupRequired || tv.fieldTesting?.length || tv.testAgency || tv.passFailStatus) p.testingVerification = { mockupRequired: tv.mockupRequired, fieldTesting: tv.fieldTesting, testAgency: tv.testAgency, passFailStatus: tv.passFailStatus, testResultSubmittal: tv.testResultSubmittal || null };
    const vc = form.visualCoverage; if (vc.elevationUrl || vc.markers?.length) p.visualCoverage = vc;
    return p;
  };

  const handleSaveDraft = async () => {
    if (!validateDraft()) { toast.error('Please fill in required fields on Step 1 before saving'); setStep(1); return; }
    setSaving(true);
    try {
      let pid = draftId;
      const payload = { productNameModel: form.productNameModel.trim(), status: 'draft', csiItemId, ...buildStepPayload() };
      if (form.manufacturerId) payload.manufacturerId = form.manufacturerId;
      if (form.countryCode) payload.countryCode = form.countryCode;
      if (form.airBarrierType) payload.airBarrierType = form.airBarrierType;
      if (form.roleOfProduct) payload.roleOfProduct = form.roleOfProduct;
      if (form.applicationLocation?.length) payload.applicationLocation = form.applicationLocation;
      if (!pid) { const res = await axios.post(`${API}/air-barrier-products/${projectId}`, payload); pid = res.data.id; setDraftId(pid); setForm(prev => ({ ...prev, qrToken: res.data.qrToken })); clearWizardState(companyId, 'new'); }
      else { await axios.patch(`${API}/air-barrier-products/${projectId}/${pid}`, payload); }
      if (form.imageFile) {
        const fd = new FormData(); fd.append('file', form.imageFile);
        const imgRes = await axios.post(`${API}/air-barrier-products/${projectId}/${pid}/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const url = imgRes.data?.url || imgRes.data?.urls?.original;
        if (url) setForm(prev => ({ ...prev, imageFile: null, imagePreview: url }));
      }
      if (submittals.isDirty) {
        await commitSubmittals(submittals, pid, projectId, 'air-barrier-products', axios, API);
        const fresh = await axios.get(`${API}/air-barrier-products/${projectId}/${pid}`);
        submittals.init(fresh.data?.submittals || []);
      }
      saveWizardState(companyId, pid, { step, draftId: pid, form: { ...form, imageFile: null } });
      initialFormRef.current = { ...form, imageFile: null };
      toast.success('Draft saved'); if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save draft'); }
    finally { setSaving(false); }
  };

  const handleFinalSave = async () => {
    const validationErrors = validateAllSteps();
    if (Object.keys(validationErrors).length > 0) {
      const targetStep = getFirstErrorStep(validationErrors);
      if (targetStep !== step) setStep(targetStep);
      toast.error('Please complete all required fields before finalizing');
      return;
    }
    setFinalizing(true);
    try {
      let pid = draftId;
      const payload = {
        manufacturerId: form.manufacturerId, productNameModel: form.productNameModel.trim(),
        countryCode: form.countryCode, airBarrierType: form.airBarrierType, roleOfProduct: form.roleOfProduct,
        applicationLocation: form.applicationLocation, status: 'active', csiItemId, ...buildStepPayload()
      };
      if (!pid) { const res = await axios.post(`${API}/air-barrier-products/${projectId}`, payload); pid = res.data.id; }
      else { await axios.patch(`${API}/air-barrier-products/${projectId}/${pid}`, payload); }
      if (form.imageFile) { const fd = new FormData(); fd.append('file', form.imageFile); await axios.post(`${API}/air-barrier-products/${projectId}/${pid}/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); }
      if (submittals.isDirty) {
        await commitSubmittals(submittals, pid, projectId, 'air-barrier-products', axios, API);
      }
      toast.success(isEdit ? 'Product updated' : 'Product created');
      clearWizardState(companyId, pid); clearWizardState(companyId, 'new');
      if (onComplete) onComplete(); resetAndClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save product'); }
    finally { setFinalizing(false); }
  };

  const handleNext = () => setStep(prev => Math.min(prev + 1, TOTAL_STEPS));
  const handleBack = () => setStep(prev => Math.max(prev - 1, 1));

  const resetAndClose = () => {
    if (draftId) clearWizardState(companyId, draftId); clearWizardState(companyId, 'new');
    setStep(1); setForm(INITIAL_FORM); initialFormRef.current = INITIAL_FORM;
    setErrors({}); setDraftId(null); setMfrSearch(''); setCountrySearch('');
    setShowCloseConfirm(false); initializedRef.current = false;
    submittals.rollback();
    onOpenChange(false);
  };
  const requestClose = () => { if (isDirty) setShowCloseConfirm(true); else resetAndClose(); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) requestClose(); }}>
      <DialogContent className="max-w-4xl p-0 flex flex-col" style={{ height: '75vh', maxHeight: '85vh', minHeight: '400px' }} data-testid="ab-add-product-wizard">
        <div className="px-6 pt-5 pb-3 border-b shrink-0">
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit Product' : 'Add Product'}</DialogTitle>
            <DialogDescription>Step {step} of {TOTAL_STEPS}: {WIZARD_STEPS[step - 1]?.title}</DialogDescription>
          </DialogHeader>
          <WizardStepper steps={WIZARD_STEPS} currentStep={step} onStepClick={setStep} />
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="ab-wizard-body">

          {/* STEP 1: Product Information */}
          {step === 1 && (
            <div className="space-y-4" data-testid="ab-wizard-step-1">
              <h4 className="font-semibold text-sm border-b pb-2 mb-3">Product Information</h4>
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleImageSelect} data-testid="ab-image-file-input" />
              <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4 items-stretch" data-testid="ab-product-info-grid">
                <div className={`border-2 border-dashed rounded-lg flex flex-col items-center justify-center relative overflow-hidden bg-slate-50 transition-colors min-h-[220px] ${errors.imageFile ? 'border-red-400 bg-red-50/30' : 'border-slate-300'}`} data-testid="ab-image-upload-area">
                  {form.imagePreview ? (
                    <><img src={form.imagePreview} alt="Preview" className="w-full h-full object-contain p-2" data-testid="ab-image-preview" onError={(e) => { e.target.style.display = 'none'; }} /><button type="button" className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 z-10" onClick={handleRemoveImage} data-testid="ab-remove-image-btn"><X className="h-3 w-3" /></button></>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-2 p-4 text-center"><ImageIcon className="h-10 w-10 text-slate-300" /><p className="text-xs text-slate-400">Product Image</p><p className="text-[10px] text-slate-400">PNG, JPG, WebP (max 5MB)</p></div>
                  )}
                  <div className="absolute bottom-0 inset-x-0 bg-white/90 border-t border-slate-200 flex items-center justify-center gap-1.5 py-1.5 px-2">
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setShowPasteModal(true)} data-testid="ab-paste-from-clipboard-btn"><ClipboardPaste className="h-3.5 w-3.5 mr-1" />Paste</Button>
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => fileInputRef.current?.click()} data-testid="ab-upload-image-btn"><Upload className="h-3.5 w-3.5 mr-1" />{form.imagePreview ? 'Change' : 'Upload'}</Button>
                  </div>
                  {errors.imageFile && <p className="absolute top-1 left-2 text-[10px] text-red-500 bg-white/80 px-1 rounded">{errors.imageFile}</p>}
                </div>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-sm">Manufacturer *</Label>
                    <div className="relative" ref={mfrRef}>
                      <div className={`flex items-center border rounded-md px-3 py-2 cursor-pointer ${errors.manufacturerId ? 'border-red-500' : 'border-input'}`} onClick={() => setMfrDropdownOpen(true)} data-testid="ab-manufacturer-select">
                        {selectedManufacturer ? (<div className="flex items-center justify-between w-full"><span className="text-sm">{selectedManufacturer.name}</span><X className="h-4 w-4 text-slate-400 hover:text-slate-600" onClick={(e) => { e.stopPropagation(); setForm(prev => ({ ...prev, manufacturerId: '' })); }} /></div>) : (<span className="text-muted-foreground text-sm">Select manufacturer...</span>)}
                      </div>
                      {mfrDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-52 overflow-auto">
                          <div className="sticky top-0 bg-white p-2 border-b"><div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input className="pl-8" placeholder="Search or add new..." value={mfrSearch} onChange={(e) => setMfrSearch(e.target.value)} autoFocus data-testid="ab-manufacturer-search-input" /></div></div>
                          <div className="py-1">
                            {filteredManufacturers.map((m) => (<div key={m.id} className={`flex items-center px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm ${m.id === form.manufacturerId ? 'bg-slate-50 font-medium' : ''}`} onClick={() => handleSelectManufacturer(m)} data-testid={`ab-manufacturer-option-${m.id}`}>{m.id === form.manufacturerId && <Check className="h-4 w-4 mr-2 text-orange-500" />}<span className={m.id === form.manufacturerId ? '' : 'ml-6'}>{m.name}</span>{m.isMine && <UserCheck className="h-3.5 w-3.5 ml-auto text-orange-400 shrink-0" title="Added by you" />}</div>))}
                            {showAddNew && (<div className="flex items-center px-3 py-2 cursor-pointer hover:bg-orange-50 text-sm text-orange-600 font-medium border-t" onClick={handleCreateManufacturer} data-testid="ab-add-new-manufacturer-btn">{creatingMfr ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}Add new: &quot;{mfrSearch.trim()}&quot;</div>)}
                            {filteredManufacturers.length === 0 && !showAddNew && (<div className="px-3 py-2 text-sm text-slate-400">No manufacturers found</div>)}
                          </div>
                        </div>
                      )}
                    </div>
                    {errors.manufacturerId && <p className="text-xs text-red-500">{errors.manufacturerId}</p>}
                  </div>
                  <FormField label="Product Name / Model" required error={errors.productNameModel}>
                    <Input placeholder="e.g., AirShield Pro 200" value={form.productNameModel} onChange={(e) => { setForm(prev => ({ ...prev, productNameModel: e.target.value })); if (e.target.value.trim()) setErrors(prev => ({ ...prev, productNameModel: null })); }} className={errors.productNameModel ? 'border-red-500' : ''} data-testid="ab-product-name-input" />
                  </FormField>
                  <div className="space-y-1">
                    <Label className="text-sm">Country of Manufacturer</Label>
                    <div className="relative" ref={countryRef}>
                      <div className={`flex items-center border rounded-md px-3 py-2 cursor-pointer ${errors.countryCode ? 'border-red-500' : 'border-input'}`} onClick={() => setCountryDropdownOpen(true)} data-testid="ab-country-select">
                        {selectedCountry ? (<div className="flex items-center justify-between w-full"><span className="text-sm">{selectedCountry.name}</span><X className="h-4 w-4 text-slate-400 hover:text-slate-600" onClick={(e) => { e.stopPropagation(); setForm(prev => ({ ...prev, countryCode: '' })); }} /></div>) : (<span className="text-muted-foreground text-sm">Select country...</span>)}
                      </div>
                      {countryDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-52 overflow-auto">
                          <div className="sticky top-0 bg-white p-2 border-b"><div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input className="pl-8" placeholder="Search country..." value={countrySearch} onChange={(e) => setCountrySearch(e.target.value)} autoFocus data-testid="ab-country-search-input" /></div></div>
                          <div className="py-1">{filteredCountries.slice(0, 50).map((c) => (<div key={c.code} className={`flex items-center px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm ${c.code === form.countryCode ? 'bg-slate-50 font-medium' : ''}`} onClick={() => handleSelectCountry(c)} data-testid={`ab-country-option-${c.code}`}>{c.code === form.countryCode && <Check className="h-4 w-4 mr-2 text-orange-500" />}<span className={c.code === form.countryCode ? '' : 'ml-6'}>{c.name}</span></div>))}</div>
                        </div>
                      )}
                    </div>
                    {errors.countryCode && <p className="text-xs text-red-500">{errors.countryCode}</p>}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-1">
                <FormField label="Air Barrier Type"><FormSelect value={form.airBarrierType} onValueChange={(v) => { setForm(prev => ({ ...prev, airBarrierType: v })); }} placeholder="Select type..." options={AIR_BARRIER_TYPES} testId="ab-type-select" /></FormField>
                <FormField label="Role of Product"><FormSelect value={form.roleOfProduct} onValueChange={(v) => { setForm(prev => ({ ...prev, roleOfProduct: v })); }} placeholder="Select role..." options={ROLE_OPTIONS} testId="ab-role-select" /></FormField>
              </div>
              <FormField label="Application Location"><FormSelect value={form.applicationLocation?.[0] || ''} onValueChange={(v) => { setForm(prev => ({ ...prev, applicationLocation: v === 'None' ? [] : [v] })); }} placeholder="Select location..." options={APPLICATION_LOCATIONS} testId="ab-app-location-select" /></FormField>
            </div>
          )}

          {/* STEP 2: Air Leakage Performance */}
          {step === 2 && (
            <div className="space-y-4" data-testid="ab-wizard-step-2">
              <h4 className="font-semibold text-sm border-b pb-2">Air Leakage Performance</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Air Permeance Value" error={errors['airLeakagePerformance.airPermeanceValue']}>
                  <Input type="text" inputMode="decimal" placeholder="e.g., 0.02" value={form.airLeakagePerformance.airPermeanceValue} onChange={(e) => updateNested('airLeakagePerformance', 'airPermeanceValue', sanitizeNumeric(e.target.value))} className={errors['airLeakagePerformance.airPermeanceValue'] ? 'border-red-500' : ''} data-testid="ab-air-permeance-input" />
                </FormField>
                <FormField label="Test Standard">
                  <FormSelect value={form.airLeakagePerformance.testStandard} onValueChange={(v) => updateNested('airLeakagePerformance', 'testStandard', v)} placeholder="Select standard..." options={LEAKAGE_TEST_STANDARDS} testId="ab-test-standard-select" />
                </FormField>
                <FormField label="Test Pressure (Pa)" error={errors['airLeakagePerformance.testPressure']}>
                  <Input type="text" inputMode="decimal" placeholder="e.g., 75" value={form.airLeakagePerformance.testPressure} onChange={(e) => updateNested('airLeakagePerformance', 'testPressure', sanitizeNumeric(e.target.value))} className={errors['airLeakagePerformance.testPressure'] ? 'border-red-500' : ''} data-testid="ab-test-pressure-input" />
                </FormField>
                <FormField label="Compliance Statement">
                  <Input type="text" placeholder="e.g., Meets ASHRAE 90.1 requirements" value={form.airLeakagePerformance.complianceStatement} onChange={(e) => updateNested('airLeakagePerformance', 'complianceStatement', e.target.value)} data-testid="ab-compliance-statement-input" />
                </FormField>
              </div>
            </div>
          )}

          {/* STEP 3: Continuity & System Components */}
          {step === 3 && (
            <div className="space-y-4" data-testid="ab-wizard-step-3">
              <h4 className="font-semibold text-sm border-b pb-2">Continuity & System Components</h4>
              <MultiSelectCheckboxes label="Required Accessories" options={REQUIRED_ACCESSORIES} selected={form.continuityComponents.requiredAccessories || []} onChange={(v) => updateNested('continuityComponents', 'requiredAccessories', v)} testId="ab-required-accessories" />
              <div className="space-y-1">
                <FormField label="Compatible Products">
                  <Input placeholder="e.g., Product A, Product B" value={form.continuityComponents.compatibleProducts} onChange={(e) => updateNested('continuityComponents', 'compatibleProducts', e.target.value)} data-testid="ab-compatible-products-input" />
                </FormField>
                <p className="text-xs text-slate-400 pl-0.5">Manufacturer Verified</p>
              </div>
              <FormField label="Continuity Method Description">
                <Input placeholder="Describe the continuity method..." value={form.continuityComponents.continuityMethodDescription} onChange={(e) => updateNested('continuityComponents', 'continuityMethodDescription', e.target.value)} data-testid="ab-continuity-method-input" />
              </FormField>
              <FormField label="Discontinuity Risk Zones">
                <Input placeholder="e.g., Window perimeters, penetrations" value={form.continuityComponents.discontinuityRiskZones} onChange={(e) => updateNested('continuityComponents', 'discontinuityRiskZones', e.target.value)} data-testid="ab-discontinuity-risk-input" />
              </FormField>
            </div>
          )}

          {/* STEP 4: Substrate & Compatibility */}
          {step === 4 && (
            <div className="space-y-4" data-testid="ab-wizard-step-4">
              <h4 className="font-semibold text-sm border-b pb-2">Substrate & Compatibility</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Approved Substrates">
                  <FormSelect value={form.substrateCompatibility.approvedSubstrates} onValueChange={(v) => updateNested('substrateCompatibility', 'approvedSubstrates', v)} placeholder="Select substrate..." options={APPROVED_SUBSTRATES} testId="ab-approved-substrates-select" />
                </FormField>
                <FormField label="Primer Required?" required error={errors['substrateCompatibility.primerRequired']}>
                  <FormSelect value={form.substrateCompatibility.primerRequired} onValueChange={(v) => updateNested('substrateCompatibility', 'primerRequired', v)} placeholder="Select..." options={['Yes', 'No']} error={errors['substrateCompatibility.primerRequired']} testId="ab-primer-required-select" />
                </FormField>
                <FormField label="Compatibility with">
                  <FormSelect value={form.substrateCompatibility.compatibilityWith} onValueChange={(v) => updateNested('substrateCompatibility', 'compatibilityWith', v)} placeholder="Select..." options={COMPATIBILITY_WITH_OPTIONS} testId="ab-compatibility-with-select" />
                </FormField>
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Manufacturer Compatibility Letter <span className="text-xs text-slate-400 font-normal ml-1">PDF only</span></Label>
                {form.substrateCompatibility.compatibilityLetterSubmittal ? (
                  <div className="flex items-center gap-2 border rounded-md p-2 text-sm">
                    <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                    <span className="flex-1 truncate">{form.substrateCompatibility.compatibilityLetterSubmittal.fileName}</span>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500" onClick={() => {
                      if (draftId && form.substrateCompatibility.compatibilityLetterSubmittal?.id) {
                        handleDeleteSubmittal(form.substrateCompatibility.compatibilityLetterSubmittal.id);
                      }
                      updateNested('substrateCompatibility', 'compatibilityLetterSubmittal', null);
                    }} data-testid="ab-compat-letter-remove-btn"><X className="h-3.5 w-3.5" /></Button>
                  </div>
                ) : (
                  <div>
                    <input ref={compatLetterRef} type="file" accept=".pdf" className="hidden" onChange={handleCompatLetterUpload} data-testid="ab-compat-letter-input" />
                    <Button variant="outline" size="sm" onClick={() => compatLetterRef.current?.click()} disabled={uploadingCompatLetter} data-testid="ab-compat-letter-upload-btn">
                      {uploadingCompatLetter ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}Upload PDF
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 5: Installation Parameters */}
          {step === 5 && (
            <div className="space-y-4" data-testid="ab-wizard-step-5">
              <h4 className="font-semibold text-sm border-b pb-2">Installation Parameters</h4>
              {/* Row 1: Application Method + conditional Other */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Application Method">
                  <FormSelect value={form.installationParameters.applicationMethod} onValueChange={(v) => updateNested('installationParameters', 'applicationMethod', v)} placeholder="Select method..." options={APPLICATION_METHODS} testId="ab-app-method-select" />
                </FormField>
                {form.installationParameters.applicationMethod === 'Other' && (
                  <FormField label="Specify Application Method" required error={errors['installationParameters.applicationMethodOther']}>
                    <Input placeholder="Specify method..." value={form.installationParameters.applicationMethodOther} onChange={(e) => updateNested('installationParameters', 'applicationMethodOther', e.target.value)} className={errors['installationParameters.applicationMethodOther'] ? 'border-red-500' : ''} data-testid="ab-app-method-other-input" />
                  </FormField>
                )}
              </div>
              {/* Row 2+: Remaining fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label={`Wet Mil Thickness${form.airBarrierType === 'Fluid-applied' ? ' *' : ''}`} error={errors['installationParameters.wetMilThickness']}>
                  <Input type="text" inputMode="decimal" placeholder="e.g., 60 or 1/4" value={form.installationParameters.wetMilThickness} onChange={(e) => updateNested('installationParameters', 'wetMilThickness', sanitizeFraction(e.target.value))} className={errors['installationParameters.wetMilThickness'] ? 'border-red-500' : ''} data-testid="ab-wet-mil-input" />
                </FormField>
                <FormField label="Cure Time">
                  <Input placeholder="e.g., 24 hours" value={form.installationParameters.cureTime} onChange={(e) => updateNested('installationParameters', 'cureTime', e.target.value)} data-testid="ab-cure-time-input" />
                </FormField>
                <FormField label="Installation Temperature Range (°F)">
                  <Input placeholder="e.g., 40°F – 120°F" value={form.installationParameters.installationTempRange} onChange={(e) => updateNested('installationParameters', 'installationTempRange', e.target.value)} data-testid="ab-temp-range-input" />
                </FormField>
                <FormField label="Max Exposure Before Covering (days)" error={errors['installationParameters.maxExposureDays']}>
                  <Input type="text" inputMode="decimal" placeholder="e.g., 30" value={form.installationParameters.maxExposureDays} onChange={(e) => updateNested('installationParameters', 'maxExposureDays', sanitizeNumeric(e.target.value))} className={errors['installationParameters.maxExposureDays'] ? 'border-red-500' : ''} data-testid="ab-max-exposure-input" />
                </FormField>
              </div>
            </div>
          )}

          {/* STEP 6: Fire & Code Compliance */}
          {step === 6 && (
            <div className="space-y-4" data-testid="ab-wizard-step-6">
              <h4 className="font-semibold text-sm border-b pb-2">Fire & Code Compliance</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="NFPA 285 Compatibility" required error={errors['fireCodeCompliance.nfpa285Compatibility']}>
                  <FormSelect value={form.fireCodeCompliance.nfpa285Compatibility} onValueChange={(v) => updateNested('fireCodeCompliance', 'nfpa285Compatibility', v)} placeholder="Select..." options={['Yes', 'No']} error={errors['fireCodeCompliance.nfpa285Compatibility']} testId="ab-nfpa285-select" />
                </FormField>
                <FormField label="Referenced Wall Assemblies">
                  <Input placeholder="e.g., UL Assembly U301" value={form.fireCodeCompliance.referencedWallAssemblies} onChange={(e) => updateNested('fireCodeCompliance', 'referencedWallAssemblies', e.target.value)} data-testid="ab-ref-wall-assemblies-input" />
                </FormField>
                <FormField label="ASTM E84 (FSI / SDI)">
                  <Input placeholder="e.g., 25/50" value={form.fireCodeCompliance.astmE84} onChange={(e) => updateNested('fireCodeCompliance', 'astmE84', e.target.value)} data-testid="ab-astm-e84-input" />
                </FormField>
                <FormField label="ICC-ES / Evaluation Report No">
                  <Input placeholder="e.g., ESR-1647" value={form.fireCodeCompliance.iccESReport} onChange={(e) => updateNested('fireCodeCompliance', 'iccESReport', e.target.value)} data-testid="ab-icc-es-input" />
                </FormField>
              </div>
            </div>
          )}

          {/* STEP 7: Interface Responsibility */}
          {step === 7 && (
            <div className="space-y-4" data-testid="ab-wizard-step-7">
              <h4 className="font-semibold text-sm border-b pb-2">Interface Responsibility</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Interfaces with">
                  <FormSelect value={form.interfaceResponsibility.interfacesWith} onValueChange={(v) => updateNested('interfaceResponsibility', 'interfacesWith', v)} placeholder="Select..." options={INTERFACES_WITH_OPTIONS} testId="ab-interfaces-with-select" />
                </FormField>
                {form.interfaceResponsibility.interfacesWith === 'Other' && (
                  <FormField label="Specify Interface" required error={errors['interfaceResponsibility.interfacesWithOther']}>
                    <Input placeholder="Specify interface..." value={form.interfaceResponsibility.interfacesWithOther} onChange={(e) => updateNested('interfaceResponsibility', 'interfacesWithOther', e.target.value)} className={errors['interfaceResponsibility.interfacesWithOther'] ? 'border-red-500' : ''} data-testid="ab-interfaces-with-other-input" />
                  </FormField>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Continuity Responsibility" required error={errors['interfaceResponsibility.continuityResponsibility']}>
                  <FormSelect value={form.interfaceResponsibility.continuityResponsibility} onValueChange={(v) => updateNested('interfaceResponsibility', 'continuityResponsibility', v)} placeholder="Select..." options={CONTINUITY_RESP_OPTIONS} error={errors['interfaceResponsibility.continuityResponsibility']} testId="ab-continuity-resp-select" />
                </FormField>
                {form.interfaceResponsibility.continuityResponsibility === 'Other' && (
                  <FormField label="Specify Subcontractor" required error={errors['interfaceResponsibility.otherSubcontractor']}>
                    <Input placeholder="Subcontractor name..." value={form.interfaceResponsibility.otherSubcontractor} onChange={(e) => updateNested('interfaceResponsibility', 'otherSubcontractor', e.target.value)} className={errors['interfaceResponsibility.otherSubcontractor'] ? 'border-red-500' : ''} data-testid="ab-other-subcontractor-input" />
                  </FormField>
                )}
              </div>
              <div className="mt-2">
                <OrangeCheckbox
                  id="ab-transition-ownership"
                  checked={!!form.interfaceResponsibility.transitionOwnershipConfirmation}
                  onChange={(e) => updateNested('interfaceResponsibility', 'transitionOwnershipConfirmation', e.target.checked)}
                  label="Transition Ownership Confirmation"
                  testId="ab-transition-ownership-checkbox"
                />
              </div>
            </div>
          )}

          {/* STEP 8: Testing & Field Verification */}
          {step === 8 && (
            <div className="space-y-4" data-testid="ab-wizard-step-8">
              <h4 className="font-semibold text-sm border-b pb-2">Testing & Field Verification</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Mock-up Required?" required error={errors['testingVerification.mockupRequired']}>
                  <FormSelect value={form.testingVerification.mockupRequired} onValueChange={(v) => updateNested('testingVerification', 'mockupRequired', v)} placeholder="Select..." options={['Yes', 'No']} error={errors['testingVerification.mockupRequired']} testId="ab-mockup-required-select" />
                </FormField>
                <FormField label="Test Agency (if applicable)">
                  <Input placeholder="e.g., Intertek, UL" value={form.testingVerification.testAgency} onChange={(e) => updateNested('testingVerification', 'testAgency', e.target.value)} data-testid="ab-test-agency-input" />
                </FormField>
              </div>
              <MultiSelectCheckboxes label="Field Testing" options={FIELD_TESTING_OPTIONS} selected={form.testingVerification.fieldTesting || []} onChange={(v) => updateNested('testingVerification', 'fieldTesting', v)} testId="ab-field-testing" />
              <FormField label="Pass / Fail Status" required error={errors['testingVerification.passFailStatus']}>
                <FormSelect value={form.testingVerification.passFailStatus} onValueChange={(v) => updateNested('testingVerification', 'passFailStatus', v)} placeholder="Select..." options={['Pass', 'Fail']} error={errors['testingVerification.passFailStatus']} testId="ab-pass-fail-select" />
              </FormField>
              <div className="mt-2">
                <Label className="text-sm mb-1.5 block">Test Result <span className="text-xs text-slate-400 font-normal ml-1">PDF only</span></Label>
                {form.testingVerification.testResultSubmittal ? (
                  <div className="flex items-center gap-2 bg-slate-50 rounded-md px-3 py-2 text-sm border" data-testid="ab-test-result-file">
                    <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                    <span className="flex-1 truncate">{form.testingVerification.testResultSubmittal.fileName}</span>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500" onClick={() => {
                      if (draftId && form.testingVerification.testResultSubmittal?.id) {
                        handleDeleteSubmittal(form.testingVerification.testResultSubmittal.id);
                      }
                      updateNested('testingVerification', 'testResultSubmittal', null);
                    }} data-testid="ab-test-result-remove-btn"><X className="h-3.5 w-3.5" /></Button>
                  </div>
                ) : (
                  <div>
                    <input ref={testResultFileRef} type="file" accept=".pdf" className="hidden" onChange={handleTestResultUpload} data-testid="ab-test-result-input" />
                    <Button variant="outline" size="sm" onClick={() => testResultFileRef.current?.click()} disabled={uploadingTestResult} data-testid="ab-test-result-upload-btn">
                      {uploadingTestResult ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}Upload PDF
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 9: Submittals */}
          {step === 9 && (
              <WizardSubmittalsStep
                displayList={submittals.displayList}
                onAddUpload={submittals.addUpload}
                onRemoveItem={submittals.removeItem}
                onUndoDelete={submittals.undoDelete}
                onPreview={handlePreviewSubmittal}
                docTypes={DOCUMENT_TYPES}
              />
          )}
          <SubmittalPreviewModal open={!!previewSubmittal} onClose={() => setPreviewSubmittal(null)} submittal={previewSubmittal} />

          {/* STEP 10: Visual Coverage */}
          {step === 10 && (
            <div className="space-y-4" data-testid="ab-wizard-step-10">
              <h4 className="font-semibold text-sm border-b pb-2 mb-3">Visual Coverage <span className="text-xs text-slate-400 font-normal">(Optional)</span></h4>
              <p className="text-xs text-slate-500 italic">Illustrative only — not part of specification</p>

              {/* Marker Type — top */}
              <FormField label="Marker Type">
                <div className="flex flex-wrap gap-2">
                  {AB_MARKER_TYPES.map(mt => {
                    const meta = AB_MARKER_META[mt];
                    const selected = form.visualCoverage.markerType === mt;
                    const count = (form.visualCoverage.markers || []).filter(m => m.type === mt).length;
                    return (
                      <Badge key={mt} variant={selected ? 'default' : 'outline'}
                        className={`cursor-pointer transition-colors ${selected ? meta.hoverBg : 'hover:bg-slate-100'}`}
                        onClick={() => setForm(prev => ({ ...prev, visualCoverage: { ...prev.visualCoverage, markerType: selected ? '' : mt } }))}
                        data-testid={`ab-marker-${mt.replace(/[\s\/–]+/g, '-').toLowerCase()}`}>
                        <span className={`inline-block w-2.5 h-2.5 mr-1.5 ${meta.shape}`}
                          style={{ backgroundColor: selected ? '#fff' : meta.color }} />
                        {mt}{count > 0 ? ` (${count})` : ''}
                      </Badge>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-400 mt-1">{form.visualCoverage.markerType ? `Click on the elevation image below to place "${form.visualCoverage.markerType}" marker` : 'Select a marker type, then click on the elevation image to place'}</p>
              </FormField>

              {/* Elevation Upload */}
              <FormField label="Elevation Upload">
                {elevationUploading && (
                  <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-2">
                    <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">Processing file...</p>
                      <p className="text-xs text-blue-600">Converting elevation drawing to image</p>
                    </div>
                  </div>
                )}
                {form.visualCoverage.elevationUrl ? (
                  <div className="border rounded-lg overflow-hidden bg-slate-50">
                    {/* Filename bar */}
                    <div className="flex items-center justify-between px-3 py-2 bg-white border-b">
                      <div className="flex items-center gap-2 text-sm min-w-0">
                        <FileText className="h-4 w-4 text-red-500 shrink-0" />
                        <span className="truncate font-medium" data-testid="ab-elevation-filename">{form.visualCoverage.elevationFileName || 'elevation'}</span>
                      </div>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0 hover:bg-red-100 hover:text-red-600"
                        onClick={() => { setForm(prev => ({ ...prev, visualCoverage: { ...prev.visualCoverage, elevationFileName: '', elevationUrl: '', elevationPublicId: '', markers: [] } })); setElevationZoom(1); }}
                        data-testid="ab-remove-elevation"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                    {/* Rendered elevation image with marker placement */}
                    <div className="relative overflow-auto" style={{ maxHeight: '420px' }} data-testid="ab-elevation-container">
                      {/* Zoom controls */}
                      <div className="sticky top-1 right-1 z-20 flex justify-end px-2 py-1">
                        <ZoomControls zoom={elevationZoom}
                          onZoomIn={() => setElevationZoom(z => Math.min(z + 0.25, 3))}
                          onZoomOut={() => setElevationZoom(z => Math.max(z - 0.25, 0.5))}
                          onReset={() => setElevationZoom(1)} />
                      </div>
                      <div className="relative" style={{ transform: `scale(${elevationZoom})`, transformOrigin: 'top left', width: '100%' }}>
                        <img src={form.visualCoverage.elevationUrl} alt="Elevation" className="w-full rounded"
                          style={{ cursor: form.visualCoverage.markerType ? 'crosshair' : 'default', display: 'block' }}
                          onClick={(e) => {
                            if (!form.visualCoverage.markerType) return;
                            const rect = e.target.getBoundingClientRect();
                            const x = ((e.clientX - rect.left) / rect.width * 100).toFixed(1);
                            const y = ((e.clientY - rect.top) / rect.height * 100).toFixed(1);
                            setForm(prev => ({ ...prev, visualCoverage: { ...prev.visualCoverage, markers: [...(prev.visualCoverage.markers || []), { type: prev.visualCoverage.markerType, x: parseFloat(x), y: parseFloat(y) }] } }));
                          }} data-testid="ab-detail-image" />
                        {(form.visualCoverage.markers || []).map((m, i) => {
                          const meta = AB_MARKER_META[m.type] || {};
                          return (
                            <div key={i}
                              className={`absolute border-2 border-white shadow-lg cursor-pointer ${meta.shape === 'rounded-sm' ? 'w-3.5 h-3.5 rounded-sm' : 'w-3 h-3 rounded-full'}`}
                              style={{ left: `${m.x}%`, top: `${m.y}%`, transform: 'translate(-50%, -50%)', backgroundColor: meta.color ? `${meta.color}d9` : 'rgba(100,100,100,0.85)' }}
                              title={`${m.type} — click to remove`}
                              onClick={(e) => { e.stopPropagation(); setForm(prev => ({ ...prev, visualCoverage: { ...prev.visualCoverage, markers: prev.visualCoverage.markers.filter((_, j) => j !== i) } })); }}
                              data-testid={`ab-marker-${i}`} />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : !elevationUploading && (
                  <div className="border-2 border-dashed rounded-lg p-6 text-center">
                    <label className="cursor-pointer text-slate-400 hover:text-orange-500 transition-colors">
                      <Upload className="h-7 w-7 mx-auto mb-1.5" />
                      <span className="text-sm font-medium">Upload Drawing</span>
                      <p className="text-xs text-slate-400 mt-0.5">PDF, JPG, or PNG files</p>
                      <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" data-testid="ab-elevation-upload" onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const isPdf = f.type === 'application/pdf';
                        const isImage = ['image/jpeg', 'image/png'].includes(f.type);
                        if (!isPdf && !isImage) { toast.error('Only PDF, JPG, and PNG files are allowed'); return; }
                        setElevationUploading(true);
                        try {
                          const fd = new FormData();
                          fd.append('file', f);
                          if (isPdf) {
                            const res = await axios.post(`${API}/projects/${projectId}/elevations/preview`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                            const firstPage = res.data?.pages?.[0];
                            if (firstPage) {
                              setForm(prev => ({ ...prev, visualCoverage: { ...prev.visualCoverage, elevationFileName: f.name, elevationUrl: firstPage.fullImageUrl || firstPage.previewUrl, elevationPublicId: firstPage.publicId } }));
                              toast.success('Elevation uploaded');
                            }
                          } else {
                            fd.append('type', 'air-barrier');
                            const res = await axios.post(`${API}/projects/${projectId}/elevations/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                            const elev = res.data?.elevation;
                            const imgUrl = elev?.imageUrl || elev?.fullImageUrl || elev?.previewUrl;
                            if (imgUrl) {
                              setForm(prev => ({ ...prev, visualCoverage: { ...prev.visualCoverage, elevationFileName: f.name, elevationUrl: imgUrl, elevationPublicId: elev?.publicId || '' } }));
                              toast.success('Elevation uploaded');
                            }
                          }
                        } catch { toast.error('Upload failed'); }
                        finally { setElevationUploading(false); e.target.value = ''; }
                      }} />
                    </label>
                  </div>
                )}
              </FormField>
            </div>
          )}

          {/* STEP 11: Review Summary */}
          {isReviewStep && (
            <div className="space-y-1" data-testid="ab-wizard-step-11">
              <h4 className="font-semibold text-sm border-b pb-2 mb-3">Review Summary</h4>
              <div className="bg-slate-50 rounded-lg p-4 divide-y divide-slate-200">
                <ReviewProductHeader
                  imagePreview={form.imagePreview}
                  qrToken={form.qrToken}
                  testIdPrefix="ab"
                  infoRows={[
                    ['Product Name', form.productNameModel],
                    ['Manufacturer', selectedManufacturer?.name],
                    ['Country of Manufacturer', selectedCountry?.name],
                    ['Air Barrier Type', form.airBarrierType || '—'],
                    ['Role of Product', form.roleOfProduct || '—'],
                    ['Application Location', form.applicationLocation?.length ? form.applicationLocation.join(', ') : '—'],
                  ]}
                />
                <div className="pt-3">
                  <ReviewSection title="Air Leakage Performance" rows={[
                    ['Air Permeance Value', form.airLeakagePerformance.airPermeanceValue || '—'],
                    ['Test Standard', form.airLeakagePerformance.testStandard === 'None' ? 'None' : (form.airLeakagePerformance.testStandard || '—')],
                    ['Test Pressure (Pa)', form.airLeakagePerformance.testPressure || '—'],
                    ['Compliance Statement', form.airLeakagePerformance.complianceStatement || '—'],
                  ]} />
                </div>
                <div className="pt-3">
                  <ReviewSection title="Continuity & System Components" rows={[
                    ['Required Accessories', form.continuityComponents.requiredAccessories?.length ? form.continuityComponents.requiredAccessories.join(', ') : '—'],
                    ['Compatible Products', form.continuityComponents.compatibleProducts || '—'],
                    ['Continuity Method Description', form.continuityComponents.continuityMethodDescription || '—'],
                    ['Discontinuity Risk Zones', form.continuityComponents.discontinuityRiskZones || '—'],
                  ]} />
                </div>
                <div className="pt-3">
                  <ReviewSection title="Substrate & Compatibility" rows={[
                    ['Approved Substrates', form.substrateCompatibility.approvedSubstrates === 'None' ? 'None' : (form.substrateCompatibility.approvedSubstrates || '—')],
                    ['Primer Required', form.substrateCompatibility.primerRequired || '—'],
                    ['Compatibility with', form.substrateCompatibility.compatibilityWith === 'None' ? 'None' : (form.substrateCompatibility.compatibilityWith || '—')],
                    ['Compatibility Letter', form.substrateCompatibility.compatibilityLetterSubmittal?.fileName || '—'],
                  ]} />
                </div>
                <div className="pt-3">
                  <ReviewSection title="Installation Parameters" rows={[
                    ['Application Method', form.installationParameters.applicationMethod === 'Other' ? `Other: ${form.installationParameters.applicationMethodOther}` : (form.installationParameters.applicationMethod || '—')],
                    ['Wet Mil Thickness', form.installationParameters.wetMilThickness || '—'],
                    ['Cure Time', form.installationParameters.cureTime || '—'],
                    ['Installation Temperature Range (°F)', form.installationParameters.installationTempRange || '—'],
                    ['Max Exposure Before Covering (days)', form.installationParameters.maxExposureDays || '—'],
                  ]} />
                </div>
                <div className="pt-3">
                  <ReviewSection title="Fire & Code Compliance" rows={[
                    ['NFPA 285 Compatibility', badge(form.fireCodeCompliance.nfpa285Compatibility)],
                    ['Referenced Wall Assemblies', form.fireCodeCompliance.referencedWallAssemblies || '—'],
                    ['ASTM E84 (FSI / SDI)', form.fireCodeCompliance.astmE84 || '—'],
                    ['ICC-ES / Evaluation Report No', form.fireCodeCompliance.iccESReport || '—'],
                  ]} />
                </div>
                <div className="pt-3">
                  <ReviewSection title="Interface Responsibility" rows={[
                    ['Interfaces with', form.interfaceResponsibility.interfacesWith === 'Other' ? `Other: ${form.interfaceResponsibility.interfacesWithOther}` : (form.interfaceResponsibility.interfacesWith || '—')],
                    ['Continuity Responsibility', form.interfaceResponsibility.continuityResponsibility || '—'],
                    ...(form.interfaceResponsibility.continuityResponsibility === 'Other' ? [['Other Subcontractor', form.interfaceResponsibility.otherSubcontractor]] : []),
                    ['Transition Ownership', badge(form.interfaceResponsibility.transitionOwnershipConfirmation ? 'Confirmed' : 'Not confirmed')],
                  ]} />
                </div>
                <div className="pt-3">
                  <ReviewSection title="Testing & Field Verification" rows={[
                    ['Mock-up Required', badge(form.testingVerification.mockupRequired)],
                    ['Field Testing', form.testingVerification.fieldTesting?.length ? form.testingVerification.fieldTesting.join(', ') : '—'],
                    ['Test Agency', form.testingVerification.testAgency || '—'],
                    ['Pass / Fail Status', badge(form.testingVerification.passFailStatus)],
                    ['Test Result', form.testingVerification.testResultSubmittal?.fileName || '—'],
                  ]} />
                </div>
                <WizardSubmittalsReview displayList={submittals.displayList} docTypes={DOCUMENT_TYPES} />
                <div className="pt-3">
                  <ReviewSection title="Visual Coverage" rows={[
                    ['Elevation PDF', form.visualCoverage.elevationFileName || '—'],
                    ['Markers', form.visualCoverage.markers?.length ? `${form.visualCoverage.markers.length} marker(s)` : '—'],
                  ]} />
                </div>
                <div className="pt-3">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-500">Status after save:</span>
                    <span className="font-semibold text-green-600">Active</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t shrink-0 flex items-center justify-between">
          <div className="flex gap-2">
            <Button variant="ghost" onClick={requestClose} data-testid="ab-wizard-cancel-btn">Cancel</Button>
            <Button variant="outline" onClick={handleSaveDraft} disabled={saving || finalizing || !isDirty} data-testid="ab-save-draft-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Save as Draft
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleBack} disabled={step === 1} data-testid="ab-wizard-back-btn"><ChevronLeft className="h-4 w-4 mr-1" />Back</Button>
            {isReviewStep ? (
              <Button onClick={handleFinalSave} disabled={finalizing || saving} data-testid="ab-final-save-btn">
                {finalizing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{isEdit ? 'Save' : 'Create Product'}
              </Button>
            ) : (
              <Button onClick={handleNext} disabled={saving} data-testid="ab-wizard-next-btn">Next<ChevronRight className="h-4 w-4 ml-1" /></Button>
            )}
          </div>
        </div>
      </DialogContent>

      <ClipboardPasteModal isOpen={showPasteModal} onClose={() => setShowPasteModal(false)} onUpload={handlePasteUpload} title="Paste Product Image" uploading={pasteUploading} />
      <ConfirmDialog open={showCloseConfirm} title="Unsaved Changes" description="You have unsaved changes. Are you sure you want to close? Your changes will be lost." confirmText="Close" variant="destructive" onConfirm={resetAndClose} onClose={() => setShowCloseConfirm(false)} />
    </Dialog>
  );
};

export default AddAirBarrierProductWizard;
